import {
  Box,
  Button,
  CircularProgress,
  Flex,
  IconButton,
  Text,
  Textarea,
  Tooltip,
} from '@lego/klik-ui';
import {
  ArrowsMaximize,
  ArrowsMinimize,
  CheckTick,
  ControlClose,
  Duplicate,
  PaperPlaneSolid,
  Share,
  Trash,
} from '@lego/klik-ui/icons';
import type { UIMessage } from '@tanstack/ai-react';
import {
  type ChangeEvent,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Markdown } from '@/components/common/Markdown';
import { useCopyToClipboard } from '@/hooks/common/useCopyToClipboard';
import type { PendingAction } from './connection';
import { getMessageText } from './utils';

interface ChatPanelProps {
  messages: ReadonlyArray<UIMessage>;
  isLoading: boolean;
  onSend: (content: string) => void | Promise<void>;
  onClose: () => void;
  onClear: () => void;
  isMaximized: boolean;
  onToggleMaximize: () => void;
  pendingActions: PendingAction[];
  resolvingId: string | null;
  onResolveAction: (action: PendingAction, decision: 'approve' | 'reject') => void | Promise<void>;
}

export const ChatPanel = ({
  messages,
  isLoading,
  onSend,
  onClose,
  onClear,
  isMaximized,
  onToggleMaximize,
  pendingActions,
  resolvingId,
  onResolveAction,
}: ChatPanelProps) => {
  return (
    <Flex
      background="white"
      borderRadius={isMaximized ? 'none' : 'xl'}
      direction="column"
      height={isMaximized ? '100%' : '520px'}
      maxHeight="100%"
      maxWidth="100%"
      overflow="hidden"
      width={isMaximized ? '100%' : '400px'}
    >
      <Header
        isMaximized={isMaximized}
        messages={messages}
        onClear={onClear}
        onClose={onClose}
        onToggleMaximize={onToggleMaximize}
      />
      <Thread isLoading={isLoading} messages={messages} />
      <ApprovalList
        actions={pendingActions}
        onResolve={onResolveAction}
        resolvingId={resolvingId}
      />
      <Composer isLoading={isLoading} onSend={onSend} />
    </Flex>
  );
};

interface HeaderProps {
  messages: ReadonlyArray<UIMessage>;
  onClose: () => void;
  onClear: () => void;
  isMaximized: boolean;
  onToggleMaximize: () => void;
}

const Header = ({ messages, onClose, onClear, isMaximized, onToggleMaximize }: HeaderProps) => (
  <Flex
    alignItems="center"
    background="light-blue.600"
    justifyContent="space-between"
    paddingX={4}
    paddingY={3}
  >
    <Text color="white" fontSize="sm" fontWeight="bold">
      Assistant
    </Text>
    <Flex alignItems="center" gap={2}>
      <CopyChatButton messages={messages} />
      <HeaderIconButton icon={<Trash />} label="Clear chat" onClick={onClear} />
      <HeaderIconButton
        icon={isMaximized ? <ArrowsMinimize /> : <ArrowsMaximize />}
        label={isMaximized ? 'Restore chat size' : 'Maximize chat'}
        onClick={onToggleMaximize}
      />
      <HeaderIconButton icon={<ControlClose />} label="Close chat" onClick={onClose} />
    </Flex>
  </Flex>
);

interface HeaderIconButtonProps {
  icon: ReactElement;
  label: string;
  onClick: () => void;
}

const HeaderIconButton = ({ icon, label, onClick }: HeaderIconButtonProps) => (
  <Tooltip label={label}>
    <IconButton
      _hover={{ background: 'transparent' }}
      aria-label={label}
      color="white"
      icon={icon}
      onClick={onClick}
      size="xs"
      variant="ghost"
    />
  </Tooltip>
);

const CopyChatButton = ({ messages }: { messages: ReadonlyArray<UIMessage> }) => {
  const { copied, copy } = useCopyToClipboard();

  const handleCopy = () => {
    const transcript = messages
      .map((m) => `${m.role === 'user' ? 'You' : 'Assistant'}: ${getMessageText(m.parts)}`)
      .join('\n\n');

    if (transcript) void copy(transcript);
  };

  return (
    <HeaderIconButton
      icon={copied ? <CheckTick /> : <Share />}
      label={copied ? 'Copied!' : 'Copy full chat'}
      onClick={handleCopy}
    />
  );
};

interface ThreadProps {
  messages: ReadonlyArray<UIMessage>;
  isLoading: boolean;
}

const Thread = ({ messages, isLoading }: ThreadProps) => {
  const isEmpty = messages.length === 0;
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const lastMessage = messages.at(-1);
  // The assistant reply is still streaming while the run is loading and the last message is its own.
  const streamingId = isLoading && lastMessage?.role === 'assistant' ? lastMessage.id : null;

  const pinnedRef = useRef(true);

  // A new message snaps the view back down, even if the user had scrolled away.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || messages.length === 0) return;
    pinnedRef.current = true;
    el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  // Stay glued to the bottom while pinned. The reveal grows content every frame, so a ResizeObserver
  // on the inner wrapper does the sticking — the scroller's own box never changes size.
  useEffect(() => {
    const el = containerRef.current;
    const content = contentRef.current;
    if (!el || !content) return;

    let prevHeight = el.scrollHeight;
    const onScroll = () => {
      // A scroll coinciding with a height change is layout (reveal growth, our own pin, or markdown
      // reflow clamping scrollTop), not intent — acting on it kills the follow mid-response.
      if (el.scrollHeight !== prevHeight) {
        prevHeight = el.scrollHeight;
        return;
      }
      pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };
    // The only intent signal that survives while height is churning mid-stream.
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) pinnedRef.current = false;
    };
    const observer = new ResizeObserver(() => {
      if (pinnedRef.current) el.scrollTop = el.scrollHeight;
    });

    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('wheel', onWheel, { passive: true });
    observer.observe(content);
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('wheel', onWheel);
      observer.disconnect();
    };
  }, []);

  return (
    <Flex
      direction="column"
      flex={1}
      minHeight={0}
      overflowY="auto"
      overscrollBehavior="contain"
      ref={containerRef}
    >
      {/* Padding sits on the inner content, not the scroller, whose own padding-bottom is dropped from
          scroll overflow. flexShrink={0} stops it shrinking below its content and clipping that padding. */}
      <Flex direction="column" flexGrow={1} flexShrink={0} gap={2} padding={4} ref={contentRef}>
        {isEmpty && !isLoading && (
          <Flex alignItems="center" flex={1} justifyContent="center" paddingX={4}>
            <Text color="slate.500" fontSize="sm" textAlign="center">
              Ask anything about EMMA. The assistant can see the data on the page you are currently
              on.
            </Text>
          </Flex>
        )}

        {messages.map((message) =>
          message.role === 'user' ? (
            <UserMessage key={message.id} text={getMessageText(message.parts)} />
          ) : (
            <AssistantMessage
              isStreaming={message.id === streamingId}
              key={message.id}
              text={getMessageText(message.parts)}
            />
          )
        )}

        {isLoading && lastMessage?.role !== 'assistant' && <ThinkingBubble />}
      </Flex>
    </Flex>
  );
};

const CopyMessageButton = ({ text }: { text: string }) => {
  const { copied, copy } = useCopyToClipboard();

  return (
    <Tooltip label={copied ? 'Copied!' : 'Copy message'}>
      <IconButton
        aria-label="Copy message"
        color="slate.500"
        icon={copied ? <CheckTick /> : <Duplicate />}
        onClick={() => text && void copy(text)}
        size="xs"
        variant="ghost"
      />
    </Tooltip>
  );
};

const UserMessage = ({ text }: { text: string }) => (
  <Flex alignItems="flex-end" direction="column" role="group">
    <Box
      background="light-blue.600"
      borderBottomLeftRadius="lg"
      borderBottomRightRadius="xs"
      borderTopLeftRadius="lg"
      borderTopRightRadius="lg"
      boxShadow="sm"
      color="white"
      fontSize="sm"
      maxWidth="85%"
      paddingX={3}
      paddingY={2}
    >
      <Text color="inherit" fontSize="sm">
        {text}
      </Text>
    </Box>
    <MessageActions text={text} />
  </Flex>
);

// Shared chrome for assistant-side bubbles, so the streamed reply and the thinking placeholder can't
// drift apart visually.
const AssistantBubble = ({ children }: { children: ReactNode }) => (
  <Box
    background="slate.100"
    borderBottomLeftRadius="xs"
    borderBottomRightRadius="lg"
    borderTopLeftRadius="lg"
    borderTopRightRadius="lg"
    boxShadow="sm"
    fontSize="sm"
    maxWidth="85%"
    paddingX={3}
    paddingY={2}
  >
    {children}
  </Box>
);

const AssistantMessage = ({
  text,
  isStreaming = false,
}: {
  text: string;
  isStreaming?: boolean;
}) => {
  const revealed = useSmoothReveal(text);
  const isRevealing = revealed < text.length;
  const displayText = text.slice(0, revealed);
  return (
    <Flex alignItems="flex-start" direction="column" role="group">
      <AssistantBubble>
        <Markdown>{displayText}</Markdown>
        {(isStreaming || isRevealing) && (
          <AssistantSpinner marginTop={2} testId="streaming-indicator" />
        )}
      </AssistantBubble>
      {!isStreaming && !isRevealing && <MessageActions text={text} />}
    </Flex>
  );
};

/**
 * Reveals `text` at a smooth, capped rate so display speed is decoupled from bursty network cadence
 * rather than jumping as raw stream chunks land. Seeds to the text present on mount, so historical
 * messages render instantly while a live one animates from its first token.
 */
const useSmoothReveal = (text: string) => {
  const [revealed, setRevealed] = useState(() => text.length);
  // `count` is the source of truth; `prev` stays continuous across token updates so appending text
  // doesn't stall a frame each time.
  const state = useRef({ raf: 0, prev: 0, count: text.length });

  useEffect(() => {
    cancelAnimationFrame(state.current.raf);
    const tick = (now: number) => {
      const s = state.current;
      // Cap the frame delta so a long idle gap (rAF paused, or a caught-up stretch) can't jump on resume.
      const dt = s.prev ? Math.min((now - s.prev) / 1000, 0.05) : 0;
      s.prev = now;
      if (s.count >= text.length) return;
      // Speed scales with the backlog so the tail drains in ~0.7s, but the 160 cps cap is what stops a
      // token burst dumping as a chunk; the 75 cps floor keeps idle stretches moving.
      const speed = Math.min(160, Math.max(75, (text.length - s.count) / 0.7));
      s.count = Math.min(text.length, s.count + speed * dt);
      setRevealed(Math.floor(s.count));
      s.raf = requestAnimationFrame(tick);
    };
    state.current.raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(state.current.raf);
  }, [text]);

  return revealed;
};

const AssistantSpinner = ({ marginTop, testId }: { marginTop?: number; testId: string }) => (
  <Box data-testid={testId} marginTop={marginTop}>
    <CircularProgress
      color="slate.400"
      dimensions={5}
      isIndeterminate
      thickness={0.75}
      trackColor="slate.200"
    />
  </Box>
);

const MessageActions = ({ text }: { text: string }) => (
  <Box _groupHover={{ opacity: 1 }} opacity={0} transition="opacity 120ms ease-out">
    <CopyMessageButton text={text} />
  </Box>
);

const ThinkingBubble = () => (
  <Flex alignItems="flex-start" direction="column">
    <AssistantBubble>
      <AssistantSpinner testId="thinking-indicator" />
    </AssistantBubble>
  </Flex>
);

interface ApprovalListProps {
  actions: PendingAction[];
  resolvingId: string | null;
  onResolve: (action: PendingAction, decision: 'approve' | 'reject') => void | Promise<void>;
}

const ApprovalList = ({ actions, resolvingId, onResolve }: ApprovalListProps) => {
  if (actions.length === 0) return null;

  return (
    <Flex
      borderTop="1px solid"
      borderTopColor="slate.200"
      direction="column"
      gap={2}
      maxHeight="240px"
      overflowY="auto"
      overscrollBehavior="contain"
      padding={3}
    >
      {actions.map((action) => (
        <ApprovalCard
          action={action}
          isDisabled={resolvingId !== null}
          isResolving={resolvingId === action.actionId}
          key={action.actionId}
          onResolve={onResolve}
        />
      ))}
    </Flex>
  );
};

interface ApprovalCardProps {
  action: PendingAction;
  isResolving: boolean;
  isDisabled: boolean;
  onResolve: (action: PendingAction, decision: 'approve' | 'reject') => void | Promise<void>;
}

const ApprovalCard = ({ action, isResolving, isDisabled, onResolve }: ApprovalCardProps) => (
  <Box
    background="warning.50"
    border="1px solid"
    borderColor="warning.200"
    borderRadius="lg"
    padding={3}
  >
    <Text fontSize="sm" fontWeight="bold">
      Approval required: {action.tool}
    </Text>
    {action.summary && (
      <Box fontSize="sm" marginTop={1}>
        <Markdown>{action.summary}</Markdown>
      </Box>
    )}
    <Flex gap={2} marginTop={3}>
      <Button
        isDisabled={isDisabled}
        isLoading={isResolving}
        onClick={() => onResolve(action, 'approve')}
        size="sm"
      >
        Approve
      </Button>
      <Button
        isDisabled={isDisabled}
        onClick={() => onResolve(action, 'reject')}
        size="sm"
        variant="ghost"
      >
        Reject
      </Button>
    </Flex>
  </Box>
);

interface ComposerProps {
  isLoading: boolean;
  onSend: (content: string) => void | Promise<void>;
}

const Composer = ({ isLoading, onSend }: ComposerProps) => {
  const [value, setValue] = useState('');
  const isEmpty = value.trim().length === 0;

  const submit = () => {
    if (isEmpty || isLoading) return;
    void onSend(value.trim());
    setValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Box borderTop="1px solid" borderTopColor="slate.200" padding={3}>
      <Flex
        alignItems="center"
        background="slate.30"
        borderRadius="lg"
        gap={2}
        paddingX={3}
        paddingY={2}
      >
        <Textarea
          _focus={{ boxShadow: 'none' }}
          background="transparent"
          border="none"
          boxShadow="none"
          flex={1}
          fontSize="sm"
          minHeight="unset"
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          overscrollBehavior="contain"
          paddingX={0}
          paddingY={0}
          placeholder="Ask about EMMA..."
          resize="none"
          rows={1}
          value={value}
          variant="unstyled"
        />
        <IconButton
          aria-label="Send message"
          color={isEmpty || isLoading ? 'slate.400' : 'light-blue.600'}
          icon={<PaperPlaneSolid />}
          isDisabled={isEmpty || isLoading}
          onClick={submit}
          size="xs"
          transition="all 0.2s ease-out"
          variant="ghost"
        />
      </Flex>
    </Box>
  );
};
